import { i18n, baseLocales } from '@fs/zion-locale'
import { useCanAddPersonalData } from '@fs/zion-permissions'
import { baseUrl } from '@fs/zion-config'
import useFamAppsLinks from './useFamAppsLinks'
import useGenResourcesLinks from './useGenResourcesLinks'
import useGetHelpLinks from './useGetHelpLinks'
import useIndustryLinks from './useIndustryLinks'
import useContributeLinks from './useContributeLinks'
import useLearningLinks from './useLearningLinks'
import useLocationsLinks from './useLocationsLinks'
import useOfferLinks from './useOfferLinks'
import useTempleLinks from './useTempleLinks'
import useAboutLinks from './useAboutLinks'
import useThirdPartyAccessLinks from './useThirdPartyAccessLinks'
import usePopularLinks from './usePopularLinks'

export default function useLinks({ nonReligiousMode, canSeeTemple, external }) {
  const [canAddPersonalData] = useCanAddPersonalData()
  const isCoreLanguage = baseLocales.includes(i18n.language)
  const isNonEnCoreLanguage = isCoreLanguage && i18n.language !== 'en'
  const aboutLinks = useAboutLinks({ nonReligiousMode, isNonEnCoreLanguage, isCoreLanguage })
  const famAppsLinks = useFamAppsLinks({ isCoreLanguage, nonReligiousMode, canAddPersonalData })
  const genResourcesLinks = useGenResourcesLinks({
    nonReligiousMode,
    isNonEnCoreLanguage,
    isCoreLanguage,
    canAddPersonalData,
  })
  const getHelpLinks = useGetHelpLinks({ nonReligiousMode, isCoreLanguage, canAddPersonalData })
  const industryLinks = useIndustryLinks({ nonReligiousMode, isNonEnCoreLanguage, isCoreLanguage })
  const contributeLinks = useContributeLinks({
    nonReligiousMode,
    isNonEnCoreLanguage,
    canAddPersonalData,
    isCoreLanguage,
  })
  const learningLinks = useLearningLinks({ nonReligiousMode, isCoreLanguage, canAddPersonalData })
  const locationsLinks = useLocationsLinks({ isCoreLanguage })
  const offerLinks = useOfferLinks({ nonReligiousMode, isCoreLanguage, canAddPersonalData })
  const popularLinks = usePopularLinks({ isNonEnCoreLanguage })
  const templeLinks = useTempleLinks({ nonReligiousMode, canAddPersonalData, canSeeTemple })
  const thirdPartyAccessLinks = useThirdPartyAccessLinks({
    canSeeTemple,
    isNonEnCoreLanguage,
    isCoreLanguage,
    canAddPersonalData,
  })

  /** ATTENTION: The design wants vertical alignment but expects horizontal flow. In order to keep the orientation vertical for the flowGrid,
   * different sets of links are passed in depending on canSeeTemple * */

  // Correct order for when canSeeTemple = false
  const generalOrderedLinks = [
    templeLinks,
    offerLinks,
    contributeLinks,
    popularLinks,
    learningLinks,
    getHelpLinks,
    genResourcesLinks,
    locationsLinks,
    industryLinks,
    aboutLinks,
    famAppsLinks,
  ]

  // Correct order for when canSeeTemple = true
  const memberOrderedLinks = [
    templeLinks,
    aboutLinks,
    industryLinks,
    offerLinks,
    contributeLinks,
    popularLinks,
    famAppsLinks,
    learningLinks,
    getHelpLinks,
    genResourcesLinks,
    locationsLinks,
  ]

  let links

  if (canSeeTemple) {
    links = memberOrderedLinks
  } else {
    links = generalOrderedLinks
  }

  links.push(thirdPartyAccessLinks)

  // if hf-inj-react is using this on a different domain
  // prepend the base URL to any relative links
  if (external) {
    links.forEach((link) => {
      if (link.subNav) {
        link.subNav = link.subNav.map((subLink) => ({
          ...subLink,
          url: subLink.url.startsWith('/') ? `${baseUrl}${subLink.url}` : subLink.url,
        }))
      }
    })
  }

  return links
}
